"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable, DataTableColumn } from "mantine-datatable";
import CustomerPricingModal from "../components/customer-pricing-modal";
import CustomerPricingService, { CustomerPricing, PricingCustomer } from "../services/customer-pricing-service";
import { CustomerService, Customer } from "../../customer/services/customer-service";
import { BranchService, Branch } from "../../branch/services/branch-service";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import Tippy from "@tippyjs/react";
import { IcTrash, IconEye, IconEdit } from "../../../components/icons/icon-group";
import { PAGE_SIZES } from "../../../constants/status";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import { useDebounce } from "../../../hooks/use-debounce";
import EmptyState from "../../../components/custom/empty-state";
import { formatCurrency } from "../../../utils/helpers";

// Grouped customer data type
interface CustomerGroup {
  customer_id: number;
  customer_name: string;
  customer_phone?: string;
  pricing_count: number;
  /** Chi tiết giá của khách — chỉ tải khi mở dòng; `undefined` = chưa tải xong. */
  pricings?: CustomerPricing[];
}

/** Số khách tối đa lấy về cho 1 lần tìm — bằng giới hạn `limit` của API danh sách khách hàng. */
const SEARCH_CUSTOMER_LIMIT = 500;

/** Chuẩn hoá data của API danh sách giá (mảng thẳng hoặc `{ list }`). */
const toPricingRows = (data: unknown): CustomerPricing[] => {
  if (Array.isArray(data)) return data as CustomerPricing[];
  if (data && typeof data === "object" && "list" in data) return ((data as any).list || []) as CustomerPricing[];
  return [];
};

export const CustomerPricingIndexPage: React.FC = () => {
  // Phân trang phía BE theo KHÁCH HÀNG (mỗi dòng 1 khách); chi tiết giá từng khách tải riêng khi mở dòng.
  const [groups, setGroups] = useState<PricingCustomer[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [detailsByCustomer, setDetailsByCustomer] = useState<Record<number, CustomerPricing[]>>({});
  const requestedDetailIdsRef = useRef(new Set<number>());
  // Bảng cao vừa tới đáy viewport — thanh phân trang nằm TRONG DataTable nên không cần chừa thêm.
  const { ref: frameRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<CustomerPricing | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [expandedCustomerIds, setExpandedCustomerIds] = useState<number[]>([]);

  // Tìm khách hàng theo mã / tên / SĐT. Tên khách nằm ở customer service nên tìm 2 bước: hỏi customer
  // service lấy id các khách khớp → gửi id sang product service để lọc + phân trang.
  const [search, setSearch] = useState("");
  // Chờ ngừng gõ 500ms mới tìm — mỗi ký tự là 2 request (customer + product).
  const debouncedSearch = useDebounce(search, 500);
  // null = không tìm (lấy tất cả); mảng = chỉ các khách này (rỗng = không khách nào khớp).
  const [searchCustomerIds, setSearchCustomerIds] = useState<number[] | null>(null);
  const [searching, setSearching] = useState(false);
  const searchRequestRef = useRef(0);

  // Edit state
  const [editingItem, setEditingItem] = useState<CustomerPricing | null>(null);
  const [editPrice, setEditPrice] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Data for dropdowns
  const [customers, setCustomers] = useState<Array<{ id: number; name: string; phone?: string; default_branch_id?: number }>>([]);
  const [branches, setBranches] = useState<Array<{ id: number; name: string }>>([]);

  // Services
  const { fetch: listPricing } = CustomerPricingService.list();
  const { fetch: listPricingCustomers } = CustomerPricingService.listCustomers();
  const { fetch: deletePricing, loading: deleting } = CustomerPricingService.delete();
  const { fetch: singleSave } = CustomerPricingService.singleSave();
  const { fetch: listCustomers } = CustomerService.list();
  const { fetch: listBranches } = BranchService.listActive();

  // Store refs to prevent infinite loops
  const listPricingRef = useRef(listPricing);
  const listPricingCustomersRef = useRef(listPricingCustomers);
  const listCustomersRef = useRef(listCustomers);
  const listBranchesRef = useRef(listBranches);
  const singleSaveRef = useRef(singleSave);

  useEffect(() => {
    listPricingRef.current = listPricing;
    listPricingCustomersRef.current = listPricingCustomers;
    listCustomersRef.current = listCustomers;
    listBranchesRef.current = listBranches;
    singleSaveRef.current = singleSave;
  }, [listPricing, listPricingCustomers, listCustomers, listBranches, singleSave]);

  // Tải 1 trang khách hàng có giá riêng
  const load = useCallback(async () => {
    // Đang tìm mà không khách nào khớp → khỏi gọi product service.
    if (searchCustomerIds && searchCustomerIds.length === 0) {
      setGroups([]);
      setTotalRecords(0);
      return;
    }
    setLoadingList(true);
    const res = await listPricingCustomersRef.current({
      page,
      limit,
      ...(searchCustomerIds ? { customer_ids: searchCustomerIds.join(",") } : {}),
    });
    if (res && isSuccess(res)) {
      const list = res.data?.list ?? [];
      // Vừa xoá hết dòng của trang cuối → lùi về trang trước (effect sẽ tải lại).
      if (!list.length && page > 1) {
        setPage(page - 1);
        return;
      }
      setGroups(list);
      setTotalRecords(Number(res.data?.total_record) || 0);
    }
    setLoadingList(false);
  }, [page, limit, searchCustomerIds]);

  // Bước 1 của tìm kiếm: từ khoá → id các khách khớp (customer service tìm theo mã / tên / SĐT).
  useEffect(() => {
    const keyword = debouncedSearch.trim();
    const requestId = ++searchRequestRef.current;
    if (!keyword) {
      setSearching(false);
      setSearchCustomerIds(null);
      setPage(1);
      return;
    }
    setSearching(true);
    void (async () => {
      const res = await listCustomersRef.current({ key_search: keyword, page: 1, limit: SEARCH_CUSTOMER_LIMIT });
      // Người dùng đã gõ tiếp → bỏ kết quả cũ, tránh request về trễ đè lên kết quả mới.
      if (requestId !== searchRequestRef.current) return;
      const matched: Customer[] = res && isSuccess(res) ? (res.data as any)?.list ?? [] : [];
      // Gộp khách vừa tìm được vào danh sách tra tên, để dòng nào khớp cũng hiện đúng tên.
      setCustomers((prev) => {
        const byId = new Map(prev.map((c) => [Number(c.id), c]));
        matched.forEach((c) =>
          byId.set(Number(c.id), { id: c.id, name: c.name, phone: c.phone, default_branch_id: c.default_branch_id }),
        );
        return Array.from(byId.values());
      });
      setSearchCustomerIds(matched.map((c) => Number(c.id)));
      setPage(1);
      setSearching(false);
    })();
  }, [debouncedSearch]);

  // Tải chi tiết giá của MỘT khách (khi mở dòng, hoặc sau khi sửa/xoá giá của khách đó)
  const loadDetails = useCallback(async (customerId: number) => {
    requestedDetailIdsRef.current.add(customerId);
    const res = await listPricingRef.current({ customer_id: customerId });
    if (res && isSuccess(res)) {
      setDetailsByCustomer((prev) => ({ ...prev, [customerId]: toPricingRows(res.data) }));
    } else {
      // Cho phép thử lại ở lần mở dòng kế tiếp.
      requestedDetailIdsRef.current.delete(customerId);
    }
  }, []);

  // Mở dòng nào thì tải chi tiết dòng đó (mỗi khách chỉ tải 1 lần cho tới khi có thay đổi)
  useEffect(() => {
    expandedCustomerIds.forEach((id) => {
      if (!requestedDetailIdsRef.current.has(id)) void loadDetails(id);
    });
  }, [expandedCustomerIds, loadDetails]);

  // Sau khi lưu từ modal thêm/sửa (không biết khách nào đổi): tải lại trang + chi tiết các dòng đang mở
  const reloadAll = useCallback(() => {
    void load();
    requestedDetailIdsRef.current = new Set();
    setDetailsByCustomer({});
    expandedCustomerIds.forEach((id) => void loadDetails(id));
  }, [load, loadDetails, expandedCustomerIds]);

  // Load dropdown data
  const loadDropdownData = useCallback(async () => {
    // Load customers
    const customerRes = await listCustomersRef.current({ limit: 500 });
    if (customerRes && isSuccess(customerRes)) {
      const data = customerRes.data;
      if (data && typeof data === "object" && "list" in data) {
        setCustomers((data as any).list.map((c: Customer) => ({
          id: c.id,
          name: c.name,
          phone: c.phone,
          default_branch_id: c.default_branch_id,
        })));
      }
    }

    // Load branches
    const branchRes = await listBranchesRef.current();
    if (branchRes && isSuccess(branchRes)) {
      const data = branchRes.data;
      if (Array.isArray(data)) {
        setBranches(data.map((b: Branch) => ({ id: b.id, name: b.name })));
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadDropdownData();
  }, [loadDropdownData]);

  // Ghép tên khách + chi tiết giá (nếu đã tải) vào từng dòng của trang hiện tại
  const groupedData = useMemo((): CustomerGroup[] => {
    const customerById = new Map(customers.map((c) => [Number(c.id), c]));
    return groups.map((g) => {
      const customer = customerById.get(Number(g.customer_id));
      return {
        customer_id: g.customer_id,
        customer_name: customer?.name || `Khách hàng #${g.customer_id}`,
        customer_phone: customer?.phone,
        pricing_count: g.pricing_count,
        pricings: detailsByCustomer[g.customer_id],
      };
    });
  }, [groups, customers, detailsByCustomer]);

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const customerId = Number(deleteConfirm.customer_id);
    const res = await deletePricing(deleteConfirm.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Xóa giá riêng thành công", type: "success" });
      setDeleteConfirm(null);
      void load();
      void loadDetails(customerId);
      return;
    }
    showToast({ message: res?.message ?? "Không thể xóa giá riêng", type: "error" });
    setDeleteConfirm(null);
  };

  // Handle edit
  const handleStartEdit = (item: CustomerPricing) => {
    setEditingItem(item);
    // custom_price là DECIMAL (API trả chuỗi "12000.00") — qua Number để ô nhập hiện "12000", bỏ ".00" thừa.
    setEditPrice(String(Number(item.custom_price) || 0));
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditPrice("");
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    const newPrice = parseFloat(editPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      showToast({ message: "Giá không hợp lệ", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const res = await singleSaveRef.current({
        customer_id: Number(editingItem.customer_id),
        branch_id: Number(editingItem.branch_id),
        service_type_map_id: Number(editingItem.service_type_map_id),
        original_price: Number(editingItem.original_price),
        custom_price: newPrice,
      });

      if (res && isSuccess(res)) {
        showToast({ message: "Cập nhật giá thành công", type: "success" });
        void loadDetails(Number(editingItem.customer_id));
        setEditingItem(null);
        setEditPrice("");
      } else {
        showToast({ message: res?.message ?? "Không thể cập nhật giá", type: "error" });
      }
    } catch {
      showToast({ message: "Có lỗi xảy ra", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Get branch name by ID
  const getBranchName = (branchId: number) => {
    if (branchId === 0) return "Tất cả chi nhánh";
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || `Chi nhánh #${branchId}`;
  };

  // Customer columns
  const customerColumns: DataTableColumn<CustomerGroup>[] = [
    {
      accessor: "index",
      title: "#",
      textAlign: "center",
      width: 60,
      render: (_, index) => <span>{(page - 1) * limit + index + 1}</span>,
    },
    {
      accessor: "customer_name",
      title: "Khách hàng",
      render: ({ customer_name, customer_phone }) => (
        <div>
          <span className="font-medium">{customer_name}</span>
          {customer_phone && (
            <span className="text-slate-500 text-xs ml-2">{customer_phone}</span>
          )}
        </div>
      ),
    },
    {
      accessor: "pricing_count",
      title: "Số dịch vụ có giá riêng",
      textAlign: "center",
      render: ({ pricing_count }) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm bg-blue-100 text-blue-700">
          {pricing_count} dịch vụ
        </span>
      ),
    },
    {
      accessor: "action",
      title: "Thao tác",
      textAlign: "center",
      width: 100,
      render: ({ customer_id }) => (
        <Tippy content="Xem chi tiết">
          <button
            type="button"
            onClick={() => {
              setExpandedCustomerIds((prev) =>
                prev.includes(customer_id)
                  ? prev.filter((id) => id !== customer_id)
                  : [...prev, customer_id]
              );
            }}
            className="text-blue-500 hover:text-blue-700"
          >
            <IconEye />
          </button>
        </Tippy>
      ),
    },
  ];

  // Detail columns for expanded row
  const detailColumns: DataTableColumn<CustomerPricing>[] = [
    {
      accessor: "service_name",
      title: "Dịch vụ",
      render: ({ service_name, service_type_name, service_type_map_id }) => (
        <div>
          <span className="font-medium">
            {[service_name, service_type_name].filter(Boolean).join(" - ") || `Dịch vụ #${service_type_map_id}`}
          </span>
        </div>
      ),
    },
    {
      accessor: "unit_name",
      title: "ĐVT",
      textAlign: "center",
      render: ({ unit_name }) => unit_name || <span className="text-slate-400">-</span>,
    },
    {
      accessor: "branch_id",
      title: "Chi nhánh",
      render: ({ branch_id }) => getBranchName(branch_id),
    },
    {
      accessor: "original_price",
      title: "Giá gốc",
      textAlign: "right",
      render: ({ original_price }) => (
        <span className="text-slate-600">{formatCurrency(original_price)}đ</span>
      ),
    },
    {
      accessor: "custom_price",
      title: "Giá riêng",
      textAlign: "right",
      render: ({ original_price, custom_price }) => {
        const diff = Number(custom_price) - Number(original_price);
        return (
          <div>
            <span className="font-medium">{formatCurrency(custom_price)}đ</span>
            {diff !== 0 && (
              <span className={`text-xs ml-1 ${diff < 0 ? "text-green-600" : "text-red-600"}`}>
                ({diff > 0 ? "+" : ""}{formatCurrency(diff)}đ)
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessor: "action",
      title: "Thao tác",
      textAlign: "center",
      width: 100,
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <Tippy content="Sửa">
            <button
              type="button"
              onClick={() => handleStartEdit(row)}
              className="text-blue-500 hover:text-blue-700"
            >
              <IconEdit />
            </button>
          </Tippy>
          <Tippy content="Xóa">
            <button
              type="button"
              onClick={() => setDeleteConfirm(row)}
              className="text-red-500 hover:text-red-700"
            >
              <IcTrash />
            </button>
          </Tippy>
        </div>
      ),
    },
  ];

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Giá riêng theo khách hàng
        </h1>
        <button
          onClick={() => setModalOpen(true)}
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM/SỬA GIÁ RIÊNG</span>
        </button>
      </div>

      {/* Tìm khách hàng */}
      <div className="mb-4 flex items-center rounded-lg bg-slate-50 px-6 py-3">
        <div className="relative w-full sm:w-[340px]">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo mã, tên, SĐT khách hàng..."
            className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
          />
        </div>
      </div>

      {/* Customer List */}
      <div ref={frameRef} className="datatables min-h-52" style={{ height: maxHeight }}>
        <DataTable
          idAccessor="customer_id"
          withColumnBorders
          fetching={loadingList || searching}
          emptyState={
            <EmptyState
              message="Chưa có khách hàng nào có giá riêng"
              description={searchCustomerIds ? "Không tìm thấy khách hàng có giá riêng khớp từ khoá." : undefined}
            />
          }
          highlightOnHover
          className="whitespace-nowrap table-hover"
          records={groupedData}
          columns={customerColumns}
          minHeight={200}
          totalRecords={totalRecords}
          recordsPerPage={limit}
          page={page}
          onPageChange={setPage}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          paginationText={({ from, to, totalRecords }) => `Hiển thị ${from} đến ${to} của ${totalRecords} khách hàng`}
          rowExpansion={{
            allowMultiple: true,
            expanded: {
              recordIds: expandedCustomerIds,
              onRecordIdsChange: setExpandedCustomerIds,
            },
            content: ({ record }) => (
              <div className="p-4 bg-slate-50">
                <div className="text-sm font-medium text-slate-700 mb-2">
                  Chi tiết giá riêng của {record.customer_name}
                </div>
                <DataTable
                  idAccessor="id"
                  withColumnBorders
                  noRecordsText="Không có dữ liệu"
                  fetching={!record.pricings}
                  records={record.pricings ?? []}
                  columns={detailColumns}
                  minHeight={100}
                />
              </div>
            ),
          }}
        />
      </div>

      {/* Create/Edit Modal */}
      <CustomerPricingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={reloadAll}
        branches={branches}
      />

      {/* Quick Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Cập nhật giá riêng</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Dịch vụ</label>
                <div className="text-slate-800 font-medium">
                  {[editingItem.service_name, editingItem.service_type_name].filter(Boolean).join(" - ") || `Dịch vụ #${editingItem.service_type_map_id}`}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Chi nhánh</label>
                <div className="text-slate-800">{getBranchName(editingItem.branch_id)}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Giá gốc</label>
                <div className="text-slate-800">{formatCurrency(editingItem.original_price)}đ</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Giá riêng mới</label>
                <input
                  type="number"
                  min="0"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập giá mới"
                />
                {editPrice && (
                  <div className="text-sm mt-1">
                    {(() => {
                      const diff = parseFloat(editPrice) - Number(editingItem.original_price);
                      if (isNaN(diff)) return null;
                      return (
                        <span className={diff < 0 ? "text-green-600" : diff > 0 ? "text-red-600" : "text-slate-500"}>
                          Chênh lệch: {diff > 0 ? "+" : ""}{formatCurrency(diff)}đ
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editPrice}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Đang lưu..." : "Cập nhật"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Xác nhận xóa</h3>
            <p className="text-slate-600 mb-6">
              Bạn có chắc muốn xóa giá riêng này?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Đang xóa..." : "Xóa"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPricingIndexPage;
